"use client";

import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/notifications/notification-bell";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { BreadcrumbItem, useHeaderPortal } from "@/contexts/header-context";
import { cn } from "@/lib/shared/utils";
import { useAuthStore } from "@/stores/auth-store";
import { IconChevronRight, IconHome } from "@tabler/icons-react";
import Link from "next/link";
import React from "react";
import { Separator } from "../ui/separator";

interface UnifiedHeaderProps {
  className?: string;
  showBreadcrumbs?: boolean;
  variant?: "default" | "compact";
}

function Breadcrumb({
  items,
  showHome = true,
}: {
  items: BreadcrumbItem[];
  showHome?: boolean;
}) {
  if (items.length === 0 && !showHome) return null;

  const allItems = showHome
    ? [{ id: "home", title: "Home", href: "/dash", icon: IconHome }, ...items]
    : items;

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground">
      {allItems.map((item, index) => (
        <React.Fragment key={item.id}>
          {index > 0 && <IconChevronRight className="h-3 w-3 flex-shrink-0" />}
          <div className="flex items-center">
            {item.icon && <item.icon className="h-3 w-3 mr-1 flex-shrink-0" />}
            {item.href ? (
              <Link
                href={item.href}
                className="hover:text-foreground transition-colors truncate"
              >
                {item.title}
              </Link>
            ) : (
              <span
                className={cn(
                  "truncate",
                  index === allItems.length - 1 && "text-foreground font-medium"
                )}
              >
                {item.title}
              </span>
            )}
          </div>
        </React.Fragment>
      ))}
    </nav>
  );
}

export function UnifiedHeader({
  className,
  showBreadcrumbs = true,
  variant = "default",
}: UnifiedHeaderProps) {
  const { isAuthenticated } = useAuthStore();
  const {
    title,
    subtitle,
    breadcrumbs,
    toolbarContent,
    rightContent,
    actions,
  } = useHeaderPortal();

  // Don't render header if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const isCompact = variant === "compact";

  return (
    <header
      className={cn(
        "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        "flex items-center justify-between px-4 sticky top-0 z-2",
        "h-14 shrink-0 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12",
        className
      )}
    >
      {/* Left section: Sidebar trigger + Title/Breadcrumbs */}
      <div className="flex items-center space-x-4 min-w-0">
        <SidebarTrigger />
        <Separator
          orientation="vertical"
          className="mr-2 data-[orientation=vertical]:h-4"
        />
        <div className="min-w-0 flex-1">
          {showBreadcrumbs && breadcrumbs.length > 0 ? (
            <div className="space-y-1">
              <Breadcrumb showHome={false} items={breadcrumbs} />
            </div>
          ) : title ? (
            <div>
              <h1 className={cn("text-sm text-muted-foreground truncate")}>
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-muted-foreground truncate">
                  {subtitle}
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Center section: Toolbar content (flexible space) */}
      {toolbarContent && (
        <div className="flex-1 flex justify-center mx-4">{toolbarContent}</div>
      )}

      {/* Right section: Notifications + Actions + Right content */}
      <div className="flex items-center space-x-2 flex-shrink-0">
        <NotificationBell />

        {actions.map((action) => (
          <Button
            key={action.id}
            variant={action.variant || "outline"}
            size={isCompact ? "sm" : "default"}
            onClick={action.onClick}
            disabled={action.disabled}
            className={cn(isCompact && "h-8 px-3")}
          >
            {action.icon && (
              <span className="mr-2 flex-shrink-0">{action.icon}</span>
            )}
            <span className="hidden sm:inline">{action.label}</span>
          </Button>
        ))}

        {rightContent && (
          <div className="flex items-center space-x-2">{rightContent}</div>
        )}
      </div>
    </header>
  );
}
